import React, { useState, useEffect } from 'react';
import './Aismartsearch.css';
import { useNavigate } from 'react-router-dom';

const AISmartSearch = ({ products }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
    setSearchHistory(history.slice(0, 5));
  }, []);

  // Updated category mapping based on your requirements
  const categoryMappings = {
    // Chair Category - MUST BE FIRST for priority matching (IDs: 1, 2, 18)
    'chair': {
      category: 'Chair',
      productIds: [1, 2, 18],
      keywords: ['chair', 'executive chair', 'premium chair', 'sheen chair', 'office chair'],
      excludeKeywords: ['dining', 'sofa', 'bed', 'bookshelf', 'desk', 'almirah']
    },

    // Office Category - Office Desk, office chair, premium office chair (IDs: 2, 9, 18)
    'office': {
      category: 'Office',
      productIds: [2, 9, 18],
      keywords: ['office desk', 'office furniture', 'executive', 'premium office', 'workstation'],
      excludeKeywords: []
    },
    'office desk': {
      category: 'Office',
      productIds: [9],
      keywords: ['office desk', 'desk', 'workstation'],
      excludeKeywords: ['dining', 'table lamp']
    },
    'office chair': {
      category: 'Office',
      productIds: [2, 18],
      keywords: ['office chair', 'executive chair', 'premium office chair'],
      excludeKeywords: []
    },

    // Furniture Category - REMOVED 'chair' from keywords (IDs: 1, 2, 8, 9, 11, 12, 13, 17, 18)
    'furniture': {
      category: 'Furniture',
      productIds: [1, 2, 8, 9, 11, 12, 13, 17, 18],
      keywords: ['furniture', 'sofa', 'table', 'bed', 'bookshelf', 'wardrobe'], // ✅ Removed 'chair' and 'desk'
      excludeKeywords: []
    },
    'sofa': {
      category: 'Furniture',
      productIds: [8],
      keywords: ['sofa', 'couch', 'sectional'],
      excludeKeywords: []
    },
    'bed': {
      category: 'Furniture',
      productIds: [12],
      keywords: ['bed', 'mattress', 'bedframe', 'queen'],
      excludeKeywords: []
    },
    'dining table': {
      category: 'Furniture',
      productIds: [13],
      keywords: ['dining table', 'dining'],
      excludeKeywords: ['desk', 'lamp']
    },
    'bookshelf': {
      category: 'Furniture',
      productIds: [11],
      keywords: ['bookshelf', 'book self', 'shelf', 'shelving'],
      excludeKeywords: []
    },
    'wardrobe': {
      category: 'Furniture',
      productIds: [17],
      keywords: ['wardrobe', 'almirah', 'closet', 'wooden almirah'],
      excludeKeywords: []
    },

    // Decor Category - Decorative items like pots, carpets, mirrors (IDs: 3, 14, 15)
    'decor': {
      category: 'Decor',
      productIds: [3, 14, 15],
      keywords: ['decor', 'decorative', 'pot', 'carpet', 'mirror'],
      excludeKeywords: []
    },
    'pot': {
      category: 'Decor',
      productIds: [3],
      keywords: ['pot', 'plant'],
      excludeKeywords: []
    },
    'carpet': {
      category: 'Decor',
      productIds: [14],
      keywords: ['carpet', 'rug', 'mat'],
      excludeKeywords: []
    },
    'mirror': {
      category: 'Decor',
      productIds: [15],
      keywords: ['mirror', 'wall mirror'],
      excludeKeywords: []
    },

    // Art Category - Paintings and sculptures (IDs: 4, 19)
    'art': {
      category: 'Art',
      productIds: [4, 19],
      keywords: ['art', 'painting', 'sculpture'],
      excludeKeywords: []
    },
    'painting': {
      category: 'Art',
      productIds: [4],
      keywords: ['painting', 'canvas', 'frame'],
      excludeKeywords: []
    },
    'sculpture': {
      category: 'Art',
      productIds: [19],
      keywords: ['sculpture', 'monument', 'statue'],
      excludeKeywords: []
    },

    // Vehicle Category - Cars and luxury vehicles (IDs: 5, 6, 7)
    'vehicle': {
      category: 'Vehicle',
      productIds: [5, 6, 7],
      keywords: ['vehicle', 'car', 'bugatti', 'compact car', 'sport car', 'luxury car'],
      excludeKeywords: []
    },
    'car': {
      category: 'Vehicle',
      productIds: [5, 6, 7],
      keywords: ['car', 'vehicle', 'bugatti', 'compact', 'sport'],
      excludeKeywords: []
    },

    // Lighting Category - Table lamps and lighting fixtures (ID: 10)
    'lighting': {
      category: 'Lighting',
      productIds: [10],
      keywords: ['lighting', 'lamp', 'table lamp', 'light'],
      excludeKeywords: []
    },
    'lamp': {
      category: 'Lighting',
      productIds: [10],
      keywords: ['lamp', 'table lamp', 'light'],
      excludeKeywords: []
    },

    // Electronics Category - MacBook, refrigerator, washing machine (IDs: 16, 20, 21)
    'electronics': {
      category: 'Electronics',
      productIds: [16, 20, 21],
      keywords: ['electronics', 'laptop', 'macbook', 'refrigerator', 'washing machine', 'appliance'],
      excludeKeywords: []
    },
    'laptop': {
      category: 'Electronics',
      productIds: [16],
      keywords: ['laptop', 'macbook', 'computer', 'notebook'],
      excludeKeywords: []
    },
    'refrigerator': {
      category: 'Electronics',
      productIds: [20],
      keywords: ['refrigerator', 'fridge', 'freezer'],
      excludeKeywords: []
    },
    'washing machine': {
      category: 'Electronics',
      productIds: [21],
      keywords: ['washing machine', 'washer', 'laundry', 'drum'],
      excludeKeywords: []
    }
  };


  // Enhanced query analysis
  const analyzeQuery = (query) => {
    const lowerQuery = query.toLowerCase();
    const insights = {
      priceRange: null,
      detectedCategory: null,
      matchedProductIds: [],
      style: null,
      keywords: [],
      material: null,
      room: null
    };

    // Price analysis
    if (lowerQuery.includes('cheap') || lowerQuery.includes('affordable') || lowerQuery.includes('budget')) {
      insights.priceRange = { min: 0, max: 150 };
      insights.keywords.push('budget-friendly');
    } else if (lowerQuery.includes('expensive') || lowerQuery.includes('luxury') || lowerQuery.includes('premium')) {
      insights.priceRange = { min: 150, max: Infinity };
      insights.keywords.push('premium');
    } else if (lowerQuery.includes('mid-range') || lowerQuery.includes('moderate')) {
      insights.priceRange = { min: 100, max: 200 };
      insights.keywords.push('mid-range');
    }

    const priceMatch = lowerQuery.match(/under\s+(\d+)|below\s+(\d+)|less\s+than\s+(\d+)/);
    if (priceMatch) {
      const price = parseInt(priceMatch[1] || priceMatch[2] || priceMatch[3]);
      insights.priceRange = { min: 0, max: price };
      insights.keywords.push(`under $${price}`);
    }

    // Category detection with exact matching
    let bestMatch = null;
    let maxMatchScore = 0;

    for (const [, mapping] of Object.entries(categoryMappings)) {
      for (const keyword of mapping.keywords) {
        if (lowerQuery.includes(keyword)) {
          // Check if any exclude keywords are present
          const hasExclude = mapping.excludeKeywords.some(exclude => lowerQuery.includes(exclude));
          if (!hasExclude) {
            const matchScore = keyword.length;
            if (matchScore > maxMatchScore) {
              maxMatchScore = matchScore;
              bestMatch = mapping;
              insights.detectedCategory = mapping.category;
            }
          }
        }
      }
    }

    if (bestMatch) {
      insights.matchedProductIds = bestMatch.productIds;
      insights.keywords.push(insights.detectedCategory);
    }

    // Style detection
    const styles = [
      { keywords: ['modern', 'contemporary'], value: 'modern' },
      { keywords: ['minimalist', 'minimal', 'simple'], value: 'minimalist' },
      { keywords: ['classic', 'traditional', 'vintage'], value: 'classic' },
      { keywords: ['rustic', 'farmhouse', 'country'], value: 'rustic' },
      { keywords: ['industrial', 'metal'], value: 'industrial' },
      { keywords: ['luxury', 'premium', 'elegant'], value: 'luxury' }
    ];

    for (const style of styles) {
      for (const keyword of style.keywords) {
        if (lowerQuery.includes(keyword)) {
          insights.style = style.value;
          insights.keywords.push(style.value);
          break;
        }
      }
      if (insights.style) break;
    }

    // Material detection
    const materials = ['wood', 'wooden', 'metal', 'glass', 'leather', 'fabric', 'plastic', 'steel'];
    for (const material of materials) {
      if (lowerQuery.includes(material)) {
        insights.material = material;
        insights.keywords.push(material);
        break;
      }
    }

    // Room type detection
    const rooms = ['office', 'living room', 'bedroom', 'dining', 'kitchen', 'bathroom'];
    for (const room of rooms) {
      if (lowerQuery.includes(room)) {
        insights.room = room;
        insights.keywords.push(room);
        break;
      }
    }

    return insights;
  };

  // Precise AI search with category-based filtering
  const performAISearch = (query) => {
    setIsSearching(true);
    const insights = analyzeQuery(query);
    setAiInsights(insights);

    setTimeout(() => {
      let results = [];

      // If specific product IDs are matched, filter by those IDs first
      if (insights.matchedProductIds && insights.matchedProductIds.length > 0) {
        results = products.filter(product =>
          insights.matchedProductIds.includes(product.id)
        );
      } else {
        // Fallback to keyword-based search (NOT category-based)
        results = products.filter(product => {
          const productName = (product.name || '').toLowerCase();
          const productDescription = (product.description || '').toLowerCase();
          const productText = `${productName} ${productDescription}`;

          const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 2);
          const skipWords = ['the', 'a', 'an', 'and', 'or', 'for', 'with', 'in'];

          const termsMatch = searchTerms.some(term => {
            if (skipWords.includes(term)) return false;
            // ✅ FIXED: Only match in name and description, NOT category
            return productName.includes(term) || productDescription.includes(term);
          });

          return termsMatch;
        });
      }

      // Apply additional filters
      results = results.filter(product => {
        // Price filter
        if (insights.priceRange) {
          const price = parseFloat(product.price);
          if (price < insights.priceRange.min || price > insights.priceRange.max) {
            return false;
          }
        }

        // Style filter
        if (insights.style) {
          const productText = `${product.name} ${product.description || ''}`.toLowerCase();
          if (!productText.includes(insights.style)) {
            return false;
          }
        }

        // Material filter
        if (insights.material) {
          const productText = `${product.name} ${product.description || ''}`.toLowerCase();
          if (!productText.includes(insights.material)) {
            return false;
          }
        }

        return true;
      });

      // Sort by relevance
      results = results.sort((a, b) => {
        const aRelevance = calculateRelevance(a, query, insights);
        const bRelevance = calculateRelevance(b, query, insights);
        return bRelevance - aRelevance;
      });

      setSearchResults(results);
      setIsSearching(false);
      setShowSuggestions(false);

      // Update search history
      const newHistory = [query, ...searchHistory.filter(h => h !== query)].slice(0, 5);
      setSearchHistory(newHistory);
      localStorage.setItem('searchHistory', JSON.stringify(newHistory));
    }, 800);
  };


  // Relevance calculation
  const calculateRelevance = (product, query, insights) => {
    let score = 0;
    const lowerQuery = query.toLowerCase();
    const productName = product.name.toLowerCase();
    const productText = `${product.name} ${product.description || ''}`.toLowerCase();

    // Highest priority: Product ID match
    if (insights.matchedProductIds && insights.matchedProductIds.includes(product.id)) {
      score += 100;
    }

    // Exact phrase match
    if (productText.includes(lowerQuery)) {
      score += 50;
    }

    // Product name match
    if (productName.includes(lowerQuery)) {
      score += 40;
    }

    // Individual term matches
    const searchTerms = lowerQuery.split(' ').filter(term => term.length > 2);
    searchTerms.forEach(term => {
      if (productName.includes(term)) {
        score += 20;
      } else if (productText.includes(term)) {
        score += 10;
      }
    });

    // Style match
    if (insights.style && productText.includes(insights.style)) {
      score += 15;
    }

    // Material match
    if (insights.material && productText.includes(insights.material)) {
      score += 10;
    }

    // Room match
    if (insights.room && productText.includes(insights.room)) {
      score += 10;
    }

    // Price range match
    if (insights.priceRange) {
      const price = parseFloat(product.price);
      if (price >= insights.priceRange.min && price <= insights.priceRange.max) {
        score += 5;
      }
    }

    return score;
  };

  const renderProductMedia = (product) => {
    if (product.modelSrc || product.iOSSrc) {
      return (
        <model-viewer
          src={product.modelSrc}
          ios-src={product.iOSSrc}
          alt={product.name}
          auto-rotate
          camera-controls
          style={{
            width: '100%',
            height: '200px',
            background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
          }}
        />
      );
    } else if (product.image) {
      return (
        <img
          src={product.image}
          alt={product.name}
          style={{ width: '100%', height: '200px', objectFit: 'cover' }}
        />
      );
    } else {
      return (
        <div style={{
          width: '100%',
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          fontSize: '64px'
        }}>
          🛋️
        </div>
      );
    }
  };

  const smartSuggestions = [
    { icon: '🪑', text: 'Chairs', query: 'chair' },
    { icon: '🏢', text: 'Office Furniture', query: 'office' },
    { icon: '🛋️', text: 'Furniture', query: 'furniture' },
    { icon: '🎨', text: 'Decor Items', query: 'decor' },
    { icon: '💡', text: 'Lighting', query: 'lighting' },
    { icon: '🖼️', text: 'Art', query: 'art' },
    { icon: '🚗', text: 'Vehicles', query: 'vehicle' },
    { icon: '💻', text: 'Electronics', query: 'electronics' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      performAISearch(searchQuery);
    }
  };

  return (
    <div className="ai-smart-search">
      <div className="search-container">
        <form className="search-form" onSubmit={handleSubmit}>
          <div className="search-input-wrapper">
            <span className="ai-badge">AI</span>
            <input
              type="text"
              className="smart-search-input"
              placeholder="Search for furniture, electronics, cars, art..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSuggestions(e.target.value.length > 0);
              }}
              onFocus={() => setShowSuggestions(searchQuery.length > 0 || searchHistory.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            />
            <button
              type="submit"
              className="search-btn"
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? (
                <div className="loading-spinner"></div>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              )}
            </button>
          </div>
        </form>

        {showSuggestions && searchQuery && (
          <div className="search-suggestions">
            {smartSuggestions
              .filter(s => s.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
                s.query.toLowerCase().includes(searchQuery.toLowerCase()))
              .slice(0, 6)
              .map((suggestion, idx) => (
                <div
                  key={idx}
                  className="suggestion-item"
                  onMouseDown={() => {
                    setSearchQuery(suggestion.query);
                    performAISearch(suggestion.query);
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{suggestion.icon}</span>
                  <span>{suggestion.text}</span>
                </div>
              ))}
          </div>
        )}

        {showSuggestions && !searchQuery && searchHistory.length > 0 && (
          <div className="search-history">
            <div className="history-header">Recent Searches</div>
            {searchHistory.map((item, idx) => (
              <div
                key={idx}
                className="history-item"
                onMouseDown={() => {
                  setSearchQuery(item);
                  performAISearch(item);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Insights Section */}
      {aiInsights && searchResults.length > 0 && (
        <div className="ai-insights-container">
          <div className="insights-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <h3>AI Insights</h3>
          </div>

          <div className="insights-tags">
            {aiInsights.detectedCategory && (
              <span className="insight-tag category-tag">
                🏷️ {aiInsights.detectedCategory}
              </span>
            )}
            {aiInsights.style && (
              <span className="insight-tag style-tag">
                ✨ {aiInsights.style}
              </span>
            )}
            {aiInsights.priceRange && (
              <span className="insight-tag price-tag">
                💰 ${aiInsights.priceRange.min}-${aiInsights.priceRange.max === Infinity ? '∞' : aiInsights.priceRange.max}
              </span>
            )}
            {aiInsights.material && (
              <span className="insight-tag material-tag">
                🪵 {aiInsights.material}
              </span>
            )}
            {aiInsights.room && (
              <span className="insight-tag room-tag">
                🏠 {aiInsights.room}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Quick Filter Chips */}
      <div className="quick-filters">
        <div className="filter-chip" onClick={() => { setSearchQuery('furniture'); performAISearch('furniture'); }}>
          🛋️ Furniture
        </div>
        <div className="filter-chip" onClick={() => { setSearchQuery('office'); performAISearch('office'); }}>
          🏢 Office
        </div>
        <div className="filter-chip" onClick={() => { setSearchQuery('chair'); performAISearch('chair'); }}>
          🪑 Chairs
        </div>
        <div className="filter-chip" onClick={() => { setSearchQuery('decor'); performAISearch('decor'); }}>
          🎨 Decor
        </div>
        <div className="filter-chip" onClick={() => { setSearchQuery('electronics'); performAISearch('electronics'); }}>
          💻 Electronics
        </div>
        <div className="filter-chip" onClick={() => { setSearchQuery('vehicle'); performAISearch('vehicle'); }}>
          🚗 Vehicles
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="results-section">
          <h2 className="results-header">
            Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
          </h2>
          <div className="products-grid">
            {searchResults.map(product => (
              <div
                key={product.id}
                className="product-card"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                {(product.modelSrc || product.iOSSrc) && (
                  <div className="ar-badge">
                    <span>🎮</span> AR
                  </div>
                )}
                <div className="product-media">
                  {renderProductMedia(product)}
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-category">{product.category}</p>
                  <p className="product-price">${product.price}</p>
                  {product.rating && (
                    <div className="product-rating">
                      <span className="star-icon">⭐</span>
                      <span className="rating-value">{product.rating}</span>
                    </div>
                  )}
                  {product.description && (
                    <p className="product-description">
                      {product.description.substring(0, 80)}...
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results Message */}
      {searchResults.length === 0 && searchQuery && !isSearching && (
        <div className="no-results">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#ccc">
            <circle cx="11" cy="11" r="8" strokeWidth="2" />
            <path d="m21 21-4.35-4.35" strokeWidth="2" />
          </svg>
          <h3>No products found</h3>
          <p>Try different keywords or browse our suggested categories above</p>
        </div>
      )}
    </div>
  );
};

export default AISmartSearch;